import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { produtosAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package
} from 'lucide-react';
import Swal from 'sweetalert2';

export const Produtos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  usePageTitle('Produtos');  const navigate = useNavigate();

  useEffect(() => {
    fetchProdutos();
  }, []);

  const fetchProdutos = async () => {
    try {
      const response = await produtosAPI.getAll();
      setProdutos(response.data);
    } catch (error) {
      console.error('Error fetching produtos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar os produtos.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (produto) => {
    const result = await Swal.fire({
      title: 'Eliminar Produto?',
      text: `Tem a certeza que deseja eliminar "${produto.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await produtosAPI.delete(produto.id);
        setProdutos(produtos.filter(p => p.id !== produto.id));
        Swal.fire({
          icon: 'success',
          title: 'Eliminado!',
          text: 'Produto removido com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting produto:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível eliminar o produto.',
          confirmButtonColor: '#0D9488'
        });
      }
    }
  };

  const filteredProdutos = produtos.filter(produto =>
    produto.nome?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="produtos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Produtos</h1>
          <p className="text-slate-500 mt-1">Gestão de produtos e medicamentos</p>
        </div>
        <Link to="/produtos/novo">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-produto-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Produto
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome do produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-produtos-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-600" />
            Lista de Produtos ({filteredProdutos.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">A carregar...</div>
          ) : filteredProdutos.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>{search ? 'Nenhum produto encontrado' : 'Nenhum produto registado'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Nome do Produto</TableHead>
                    <TableHead className="font-semibold text-slate-700">Stock</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProdutos.map((produto) => (
                    <TableRow key={produto.id} className="hover:bg-slate-50" data-testid={`produto-row-${produto.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Package className="h-5 w-5 text-amber-600" />
                          </div>
                          {produto.nome}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          produto.quantidade > 10 
                            ? 'bg-green-50 text-green-700' 
                            : produto.quantidade > 0 
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-red-50 text-red-700'
                        }`}>
                          {produto.quantidade} unid.
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/produtos/${produto.id}/editar`)}
                            data-testid={`edit-produto-${produto.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(produto)}
                            data-testid={`delete-produto-${produto.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
